import { useEffect, useRef } from 'react';
import {
    SHARED_DATE_BLOCK_TITLE,
    SHARED_EXHAUSTED_BLOCK_MESSAGE,
    SHARED_EXHAUSTED_BLOCK_TITLE,
} from './scheduleSubmitPlan';

/**
 * 확인 창 한 번 = 시도 하나. 확인 콜백은 **자기 시도가 지금 시도이고 아직 안 쓰였을 때만** 1회 돈다.
 *
 * 참/거짓 하나로는 부족하다. 막아야 하는 것은 셋이다.
 *  1. 같은 창의 확인 연타 — 첫 실행에서 시도를 소모한다.
 *  2. 취소한 창의 늦은 콜백 — 취소 때 시도를 비운다.
 *  3. 화면을 떠난 뒤의 콜백 — 공통 알럿은 App 에 떠 있어 라우트가 바뀌어도 남는다. 언마운트 때
 *     시도를 비워, 떠난 화면의 요청이 나가지 않게 한다.
 *
 * 새 창을 열면 앞 시도는 무효가 된다(덮어쓰기). 확인 창은 화면 지역 모달(scheduleConfirmDialog)
 * 하나뿐이라 새 창이 앞 창을 대신한다. "유효한 시도가 있으면 새 창을 열지 않는다"로 하면 취소
 * 신호를 놓친 경우 화면이 영영 제출 불가로 굳는다.
 *
 * 전송 잠금(submissionLockRef)과는 별개다. 창이 떠 있는 동안에는 잠그지 않고, 확인 뒤 실제
 * 전송 직전에 기존 잠금이 그대로 잡힌다.
 */
export type ScheduleConfirmAttempt = { readonly id: number; consumed: boolean };

export type ScheduleConfirmAttemptSlot = {
    /** 새 창을 열 때 부른다. 앞 시도는 무효가 된다. */
    begin: () => ScheduleConfirmAttempt;
    /** 확인 콜백 맨 앞에서 부른다. true 일 때만 진행한다. */
    consume: (attempt: ScheduleConfirmAttempt) => boolean;
    /** 취소 — 그 시도가 지금 시도일 때만 비운다. */
    cancel: (attempt: ScheduleConfirmAttempt) => void;
    /** 화면 이탈 — 무엇이든 비운다. */
    invalidateAll: () => void;
};

export const createScheduleConfirmAttemptSlot = (): ScheduleConfirmAttemptSlot => {
    let current: ScheduleConfirmAttempt | null = null;
    let sequence = 0;
    return {
        begin: () => {
            sequence += 1;
            current = { id: sequence, consumed: false };
            return current;
        },
        consume: (attempt) => {
            if (current !== attempt || attempt.consumed) return false;
            attempt.consumed = true;
            current = null;
            return true;
        },
        cancel: (attempt) => {
            if (current === attempt) current = null;
        },
        invalidateAll: () => {
            current = null;
        },
    };
};

/** 화면 수명에 묶인 시도 슬롯. 언마운트 때 남은 시도를 무효로 만든다. */
export const useScheduleConfirmAttemptSlot = (): ScheduleConfirmAttemptSlot => {
    const slotRef = useRef<ScheduleConfirmAttemptSlot | null>(null);
    if (!slotRef.current) slotRef.current = createScheduleConfirmAttemptSlot();
    const slot = slotRef.current;
    useEffect(() => () => slot.invalidateAll(), [slot]);
    return slot;
};

/**
 * 제출 결정(resolveScheduleSubmitDecision)대로 알럿을 띄우거나 전송한다. 세 화면이 같은 것을 쓴다.
 *
 *  - 차단 두 종류(공유 소진·공유 기간 밖): 확인 버튼 하나, **전송하지 않는다.**
 *  - 확인: 화면 지역 확인 창. "그래도 잡기"일 때만 1회 전송(시도 표식).
 *  - 제출: 바로 전송.
 */
export const dispatchScheduleSubmitDecision = ({
    decision,
    createAlert,
    openConfirm,
    closeConfirm,
    send,
    buildConfirmContent,
    buildSharedDateBlockMessage,
}: {
    decision:
        | { kind: 'BLOCKED_SHARED_EXHAUSTED' }
        | { kind: 'BLOCKED_SHARED_DATE'; sharedDateWarnings: any[] }
        | { kind: 'CONFIRM'; exhausted: boolean; dateWarnings: any[] }
        | { kind: 'SUBMIT' };
    /** 공통 알럿. 콜백 없는 차단 안내에만 쓴다. */
    createAlert: (...args: any[]) => void;
    /**
     * 열려 있는 지역 확인 창을 닫고 그 시도를 무효로 만든다. 확인이 아닌 결정(차단·바로 제출)이
     * 나오면 **앞 확인 창의 "그래도 잡기"가 살아남지 않게** 먼저 부른다(교차검증 2단계: 개인 확인 →
     * 공유 차단 → 차단 안내 닫기 → 남은 확인 버튼으로 앞 요청이 나갔다).
     */
    closeConfirm: () => void;
    /** 화면 지역 확인 창(scheduleConfirmDialog). 전송 콜백은 반드시 여기로만 간다. */
    openConfirm: (
        content: { title: string; content: string; confirmText: string; cancelText: string },
        onConfirm: () => void,
    ) => void;
    send: () => void;
    buildConfirmContent: (decision: { exhausted: boolean; dateWarnings: any[] }) => {
        title: string;
        content: string;
        confirmText: string;
        cancelText: string;
    };
    buildSharedDateBlockMessage: (sharedDateWarnings: any[]) => string;
}) => {
    // 확인 창은 openConfirm 이 앞 시도를 덮어쓴다. 그 밖의 결정은 앞 시도를 여기서 끊는다.
    if (decision.kind !== 'CONFIRM') closeConfirm();
    switch (decision.kind) {
        case 'BLOCKED_SHARED_EXHAUSTED':
            createAlert(SHARED_EXHAUSTED_BLOCK_TITLE, SHARED_EXHAUSTED_BLOCK_MESSAGE, true);
            return;
        case 'BLOCKED_SHARED_DATE':
            createAlert(
                SHARED_DATE_BLOCK_TITLE,
                buildSharedDateBlockMessage(decision.sharedDateWarnings),
                true,
            );
            return;
        case 'CONFIRM':
            openConfirm(buildConfirmContent(decision), send);
            return;
        default:
            send();
    }
};
